import { useLocation } from 'react-router-dom'
import {
  useOptionalRouteCatalog,
  useOptionalRouteConfig,
} from '@features/navigation/components/RouteCatalogContext'
import { isRouteLeftOut } from '@features/navigation/logic/routeCatalog'
import { IconButton } from '@features/shared/components/IconButton'
import { HeartIcon } from '@features/shared/icons/icons'
import { useFavoriteChanges, useIsFavorite } from '../hooks/useFavorites'
import { pageFavoritePath } from '../logic/favorites'

export const ADD_TO_FAVORITES_LABEL = 'Add to favorites'
export const REMOVE_FROM_FAVORITES_LABEL = 'Remove from favorites'
/** A page's menu item that makes it the page Home and the logo open. */
export const SET_AS_HOME_PAGE_LABEL = 'Set as homepage'

/**
 * Hearts a page into the user's Favorites, or out: filled (in rose) while
 * it's a favorite, outlined while it isn't. In a sidebar row (`row`) the
 * outlined heart shows only while the row is hovered; the row decides that.
 * Its click is its own, never the row's link.
 */
export function FavoriteToggle({
  path,
  variant,
  className = '',
}: {
  path: string
  variant: 'row' | 'title'
  className?: string
}) {
  const isFavorite = useIsFavorite(path)
  const { add, remove } = useFavoriteChanges()
  const size = variant === 'row' ? 14 : 16

  return (
    <IconButton
      label={isFavorite ? REMOVE_FROM_FAVORITES_LABEL : ADD_TO_FAVORITES_LABEL}
      aria-pressed={isFavorite}
      onClick={(event) => {
        event.preventDefault()
        event.stopPropagation()
        if (isFavorite) remove(path)
        else add(path)
      }}
      className={`flex shrink-0 items-center justify-center rounded transition-colors ${
        // A row's heart takes a menu icon slot (menuIconSlots).
        variant === 'row' ? 'h-6 w-6' : 'h-[var(--titlebar-control-size,32px)] w-8'
      } ${
        isFavorite
          ? 'tone-rose text-[color:var(--tone-icon)] hover:text-[color:var(--tone-text)]'
          : variant === 'row'
            ? 'text-[color:var(--sidebar-submenu-text-faintest)] hover:text-[color:var(--sidebar-submenu-text-data)]'
            : 'text-icon-quiet hover:text-subtle'
      } ${className}`}
    >
      <HeartIcon size={size} filled={isFavorite} aria-hidden="true" />
    </IconButton>
  )
}

/**
 * The heart beside a page's title, for what the page is about (see
 * pageFavoritePath). Absent where there's no route catalog, or on a page
 * the catalog leaves out.
 */
export function PageFavoriteToggle({ tabPaths }: { tabPaths?: readonly string[] }) {
  const root = useOptionalRouteCatalog()
  const routes = useOptionalRouteConfig()
  const { pathname } = useLocation()
  const path =
    root && routes && !isRouteLeftOut(routes, pathname)
      ? pageFavoritePath(root, pathname, tabPaths)
      : null
  if (!path) return null
  return <FavoriteToggle path={path} variant="title" />
}
